import React, { useState } from 'react';
import type { StyleProp, ViewStyle } from 'react-native';

import ThemedText, { ThemedText2Props } from './themed-text-2';
import TranslationSkeletonContainer from './translation-skeleton-container';
import { useLessonContext } from '../hooks';
import useDidUpdateEffect from '../hooks/use-did-update-effect';

export interface TranslatableText2Props extends ThemedText2Props {
  translation?: string;
  skeletonGradientColor?: string;
  skeletonBackgroundColor?: string;
  skeletonStyle?: StyleProp<ViewStyle>;
  children?: React.ReactNode;
}

const TranslatableText2: React.FC<TranslatableText2Props> = ({
  translation,
  children,
  style,
  skeletonGradientColor,
  skeletonBackgroundColor,
  skeletonStyle,
  ...props
}) => {
  const animationDuration = 1200;
  const { translationActive } = useLessonContext();
  const [displayText, setDisplayText] = useState<React.ReactNode>(
    (translationActive && translation) || children
  );

  useDidUpdateEffect(() => {
    if (!translation) {
      return;
    }

    const timeout = setTimeout(() => {
      setDisplayText(translationActive ? translation : children);
    }, animationDuration / 2);

    return () => {
      clearTimeout(timeout);
    };
  }, [translationActive, children, translation]);

  return (
    <TranslationSkeletonContainer
      style={style}
      skeletonStyle={skeletonStyle}
      hasTranslation={!!translation}
      animationDuration={animationDuration}
      skeletonGradientColor={skeletonGradientColor}
      skeletonBackgroundColor={skeletonBackgroundColor}
    >
      <ThemedText style={style} {...props}>
        {displayText}
      </ThemedText>
    </TranslationSkeletonContainer>
  );
};

export default TranslatableText2;
